<template>
  <CustomDropdown
    ref="dropdownRef"
    maxHeight="300px"
    gap="10px"
    position="start"
    dropdownCSS="p-0"
    overflowY="hidden"
    :disabled="disabled"
    :relative-to-viewport="true"
    @close="handleDropdownClose"
    @triggerClick="handleTriggerClick"
  >
    <template #trigger>
      <slot name="trigger">
        <div class="secondary_small gap-1">
          <ThemeImage
            class="size-4"
            :icon="imageNames.ADD_LIST_GREY"
            alt="add_list"
          />
          Add to List
          <ThemeImage
            class="size-4"
            :icon="imageNames.DROPDOWN_GREY"
            alt="dropdown"
          />
        </div>
      </slot>
    </template>

    <template #default>
      <div v-if="showCreate" class="w-[266px]">
        <CreateListDropdownComponent
          :source="entityType"
          @entity-list-created="handleListCreated"
          @go-back="showCreate = false"
        />
      </div>
      <div v-else class="flex flex-col w-[266px] max-h-[300px]">
        <div
          class="flex flex-col py-3 px-2 flex-shrink-0"
          :class="{ border_bottom: items.length > 0 || loading }"
        >
          <span
            class="secondary_color proxima_nova font-semibold text-xs leading-[17px] px-3 pb-2"
          >
            CHOOSE A LIST
          </span>
          <CustomInput
            format="text"
            v-model="searchQuery"
            placeholder="Search here"
            type="primary"
            :icon="imageNames.SEARCH_VARIANT1"
            alt="search_icon"
            auto-focus
          />
        </div>

        <div
          v-if="items.length > 0"
          class="list_scroll_container flex flex-col p-2 overflow-y-auto flex-1 min-h-0"
          @scroll="handleScroll"
        >
          <div
            v-for="(list, index) in items"
            :key="`${list?.id}_list_${index}`"
            class="dropdown_item flex-shrink-0 flex items-center flex-row gap-2 rounded-lg"
            @click="handleListSelected(list)"
          >
            <span class="text_color truncate text-sm font-normal leading-[17px]">
              {{ list.list_name || list.name }}
            </span>
            <ThemeImage
              v-if="list?.private_flag"
              :icon="imageNames.LOCK_GREY"
              alt="private_icon"
              class="size-[14px]"
            />
          </div>
          <div v-if="loadingMore" class="flex justify-center py-2">
            <CustomLoader :size="14" />
          </div>
        </div>

        <div v-else-if="loading" class="flex justify-center py-4 flex-1">
          <CustomLoader :size="16" />
        </div>

        <div v-else-if="!loading && items.length === 0" class="flex justify-center w-full flex-1">
          <EmptyScreen
            source="list_dropdown_empty"
            text="No results found"
            css="size-12"
            padding="pt-6 pb-8"
          />
        </div>

        <div class="flex flex-col p-2 border_top flex-shrink-0">
          <div
            class="dropdown_item flex items-center flex-row gap-2 px-3 py-2 rounded-lg"
            @click="showCreate = true"
          >
            + Create New List
          </div>
        </div>
      </div>
    </template>
  </CustomDropdown>
</template>

<script setup>
import { ref } from "vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import CustomLoader from "@/src/components/CustomLoader.vue";
import CreateListDropdownComponent from "@/src/components/CreateListDropdownComponent.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import DashboardService from "@/src/api/DashboardService.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { useInfiniteScroll } from "@/src/composables/useInfiniteScroll.js";

const props = defineProps({
  entityType: { type: String, default: "lead" },
  disabled: { type: Boolean, default: false },
  lazy: { type: Boolean, default: false },
});

const emit = defineEmits(["list-selected", "list-created"]);

const dropdownRef = ref(null);
const showCreate = ref(false);

const fetchListsFn = async (page, pageSize, search) => {
  const response = await DashboardService.getAllEntityListsPaginated(
    props.entityType,
    page - 1,
    pageSize,
    search
  );
  return {
    items: response?.lists || [],
    total: response?.total || 0,
  };
};

const {
  items,
  loading,
  loadingMore,
  searchQuery,
  handleScroll,
  reset,
  refetch,
} = useInfiniteScroll({ fetchFn: fetchListsFn, pageSize: 15, debounceMs: 150, autoFetch: !props.lazy });

const handleListSelected = (list) => {
  emit("list-selected", { id: list.id, name: list.list_name || list.name });
  dropdownRef.value?.closeDropdown?.();
};

const handleListCreated = (newList) => {
  const listId = newList?.id || newList;
  const listName = newList?.list_name || newList?.name || null;
  emit("list-created", { id: listId, name: listName });
  showCreate.value = false;
  reset();
  dropdownRef.value?.closeDropdown?.();
};

const handleDropdownClose = () => {
  showCreate.value = false;
  searchQuery.value = "";
};

const handleTriggerClick = ({ isOpen }) => {
  if (!isOpen) {
    refetch();
  }
};
</script>

<style scoped>
.list_scroll_container {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.list_scroll_container::-webkit-scrollbar {
  display: none;
}
</style>
